import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Typography,
    Popover,
    IconButton,
    CircularProgress,
    TextField,
    FormControlLabel,
    Switch,
    MenuItem,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { Share, ChatBubbleOutline } from "@mui/icons-material";
import SharePopup from "../../components/ui/SharePopup/SharePopup";
import aspireProfileBackground from "../../assets/images/aspire-profile-background.png";
import { useAppDispatch } from "../../store/hook";
import { RootState, AppDispatch } from "../../store/store";
import {
    fetchFullProfile,
    fetchProfilePhoto,
    addExperience,
    updateExperience,
    deleteExperience, // Import delete action
    setEducation,
    updateEducation,
    deleteEducation, // Import delete action
    addVolunteerExperience,
    updateVolunteerExperience,
    deleteVolunteerExperience, // Import delete action
    addLicenseCertification,
    updateLicenseCertification,
    deleteLicenseCertification, // Import delete action
    updateProfile,
    setAbout,
    sendConnectionRequest,
    selectConnectionRequestStatus,
    resetConnectionStatus,
    uploadProfileBackgroundPhoto,
    fetchProfileBackgroundPhoto,
    selectProfileBackgroundPhoto,
    uploadProfilePhoto,
} from "../../store/slices/profileSlice";
import { selectProfilePhoto, selectUserLocation } from "../../store/slices/profileSlice";
import {
    ProfileState,
    Education,
    Experience,
    VolunteerExperience,
    LicenseCertification,
    fetchProfile,
    setFirstName,
    setLastName,
} from "../../store/slices/profileSlice";
import "./Profile.scss";
import { Link, useParams, useNavigate } from "react-router-dom";
import Modal from "../../components/modal/Modal";
import NewConversationModal from "../../components/Messaging/NewConversationModal";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
    fetchConnectionStatus,
    updateConnectionStatus,
    selectStatusBetweenUsers,
    selectUpdateConnectionLoading,
} from "../../store/slices/connectionSlice";
import CloudUploadIcon from "@mui/icons-material/CloudUpload"; // Import CloudUploadIcon
import { selectProfessionalStatus } from "../../store/slices/profileSlice";
import { fetchUsersForLetter } from "../../store/slices/directorySlice";
import { findOrCreateConversation, setActiveConversation } from "../../store/slices/messagingSlice";
import { fetchRandomUsersForSuggestions } from "../../store/slices/directorySlice";

// Volunteer Experience form data interface
// interface VolunteerExperienceData { ... }

// License/Certification form data interface
// interface LicenseCertificationData { ... }

// Employment type options for dropdown
const employmentTypeOptions = [
    { value: "full-time", label: "Full-time" },
    { value: "part-time", label: "Part-time" },
    { value: "contract", label: "Contract" },
    { value: "internship", label: "Internship" },
];

// Location type options for dropdown
const locationTypeOptions = [
    { value: "on-site", label: "On-site" },
    { value: "hybrid", label: "Hybrid" },
    { value: "remote", label: "Remote" },
];

// Country options for dropdown
const countryOptions = [
    { value: "usa", label: "United States" },
    { value: "canada", label: "Canada" },
    { value: "uk", label: "United Kingdom" },
    { value: "australia", label: "Australia" },
];

// Cause options for volunteer work
const causeOptions = [
    { value: "animal-welfare", label: "Animal Welfare" },
    { value: "arts-culture", label: "Arts & Culture" },
    { value: "children-youth", label: "Children & Youth" },
    { value: "community-service", label: "Community Service" },
    { value: "education", label: "Education" },
    { value: "environment", label: "Environment" },
    { value: "health-medical", label: "Health & Medical" },
    { value: "humanitarian", label: "Humanitarian Relief" },
    { value: "religious", label: "Religious" },
    { value: "social-justice", label: "Social Justice" },
    { value: "other", label: "Other" },
];
interface ProfileInformation extends Partial<ProfileState> {
    connections: number;
}

// Import the new service
import ImportProfileService from "./profileService/importProfile.service";

const Profile = () => {
    const { userId: profileUserId } = useParams();
    const dispatch: AppDispatch = useAppDispatch();
    const { user, token } = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();

    // --- Selectors for all profile-related data ---
    const loggedInUserProfile = useSelector((state: RootState) => state.profile);
    const externalProfiles = useSelector((state: RootState) => state.profile.profiles);
    const {
        items: suggestedUsers,
        status: suggestionsStatus,
        error: suggestionsError,
    } = useSelector((state: RootState) => state.directory.peopleYouMayKnow);

    // Determine if the viewed profile is the logged-in user's own profile
    const loggedInUserId = user?.id;
    const isOwnProfile = !profileUserId || profileUserId === loggedInUserId;

    // --- Single Source of Truth for Profile Data ---
    // This `useMemo` hook selects the correct profile data to display
    // based on whether the user is viewing their own profile or someone else's.
    // It prevents state pollution and acts as the single source of truth for the UI.
    const viewedProfile = useMemo(() => {
        if (isOwnProfile) {
            return loggedInUserProfile;
        }
        return externalProfiles?.[profileUserId as string]?.data;
    }, [isOwnProfile, loggedInUserProfile, externalProfiles, profileUserId]);

    // Get the loading state for the viewed profile
    const isProfileLoading = useMemo(() => {
        if (isOwnProfile) {
            return loggedInUserProfile.loading;
        }
        return externalProfiles?.[profileUserId as string]?.loading === "pending";
    }, [isOwnProfile, loggedInUserProfile.loading, externalProfiles, profileUserId]);

    // Add monitoring for messaging state
    const messagingState = useSelector((state: RootState) => state.messaging);
    useEffect(() => {
        // console.log("=== [Profile] Messaging state changed ===");
        // console.log("[Profile] activeConversationId:", messagingState.activeConversationId);
        // console.log("[Profile] conversations count:", messagingState.conversations.items.length);
        // console.log("[Profile] conversations loading:", messagingState.conversations.loading);
    }, [
        messagingState.activeConversationId,
        messagingState.conversations.items.length,
        messagingState.conversations.loading,
    ]);

    // Status for sending *initial* connection request (from profileSlice)
    const initialConnectionRequestStatus = useSelector(selectConnectionRequestStatus);

    // NEW: Status for connection between users (from connectionSlice)
    const {
        connection,
        loading: statusLoading,
        // error: statusError // Can use this to display fetch status errors
    } = useSelector(selectStatusBetweenUsers);
    const updateLoading = useSelector(selectUpdateConnectionLoading);

    // --- State for Message Modal ---
    const [isMessageModalOpen, setMessageModalOpen] = useState(false);

    // --- State for Import Profile Modal ---
    const [isImportProfileModalOpen, setImportProfileModalOpen] = useState(false);

    // NEW: State for confirmation modal
    const [isConfirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<{ type: string; index: number } | null>(null);

    // Helper function to get full name, handle potential undefined values
    const getFullName = (first?: string, last?: string) => {
        return `${first || ""} ${last || ""}`.trim();
    };

    // --- Effect for fetching all necessary profile data ---
    useEffect(() => {
        // Fetch the profile data for the user whose page is being viewed.
        if (isOwnProfile) {
            // For the user's own profile, dispatch `fetchFullProfile` if needed.
            // This thunk populates the main `state.profile` object.
            if (token && loggedInUserProfile.id !== loggedInUserId) {
                dispatch(fetchFullProfile(token));
            }
        } else if (profileUserId) {
            // For an external profile, dispatch `fetchProfile`.
            // This thunk populates the `state.profile.profiles[userId]` map.
            dispatch(fetchProfile(profileUserId));
        }

        // Fetch user suggestions for the "People You May Know" section.
        dispatch(fetchRandomUsersForSuggestions());
    }, [dispatch, profileUserId, isOwnProfile, token, loggedInUserId, loggedInUserProfile.id]);

    // NEW: Fetch connection status when profile/user changes
    useEffect(() => {
        if (!isOwnProfile && loggedInUserId && profileUserId && token) {
            dispatch(
                fetchConnectionStatus({ userId1: loggedInUserId, userId2: profileUserId, token }),
            );
        }
        // Reset initial connection request status from profileSlice when viewing a new profile
        dispatch(resetConnectionStatus());
    }, [dispatch, loggedInUserId, profileUserId, token, isOwnProfile]);

    // Scroll to top when component mounts or profileUserId changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [profileUserId]);

    // Experience modal state
    const [experienceModalOpen, setExperienceModalOpen] = useState(false);
    const [currentExperience, setCurrentExperience] = useState<Experience>({
        companyName: "",
        jobTitle: "",
        employmentType: "",
        country: "",
        locationType: "",
        currentlyWorkHere: false,
        startDate: "",
        endDate: "",
        description: "",
    });
    const [editExperienceIndex, setEditExperienceIndex] = useState<number | null>(null);
    const [experienceErrors, setExperienceErrors] = useState<
        Partial<Record<keyof Experience, string>>
    >({});

    // Education modal state
    const [educationModalOpen, setEducationModalOpen] = useState(false);
    const [currentEducation, setCurrentEducation] = useState<Education & { country?: string }>({
        school: "",
        degree: "",
        fieldOfStudy: "",
        country: "",
        grade: "",
        current: false,
        from: "",
        to: "",
        description: "",
    });
    const [editEducationIndex, setEditEducationIndex] = useState<number | null>(null);
    const [educationErrors, setEducationErrors] = useState<
        Partial<Record<keyof Education | "country", string>>
    >({}); // Include 'country' if needed

    // Volunteer Experience modal state
    const [volunteerModalOpen, setVolunteerModalOpen] = useState(false);
    const [currentVolunteer, setCurrentVolunteer] = useState<VolunteerExperience>({
        organization: "",
        role: "",
        cause: "",
        currentlyVolunteerHere: false,
        startDate: "",
        endDate: "",
        description: "",
    });
    const [editVolunteerIndex, setEditVolunteerIndex] = useState<number | null>(null);
    const [volunteerErrors, setVolunteerErrors] = useState<
        Partial<Record<keyof VolunteerExperience, string>>
    >({});

    // License/Certification modal state
    const [licenseModalOpen, setLicenseModalOpen] = useState(false);
    const [currentLicense, setCurrentLicense] = useState<LicenseCertification>({
        name: "",
        issuingOrganization: "",
        issueDate: "",
        expirationDate: "",
        credentialID: "",
        credentialURL: "",
        neverExpires: false,
    });
    const [editLicenseIndex, setEditLicenseIndex] = useState<number | null>(null);

    // Add state for About modal
    const [aboutModalOpen, setAboutModalOpen] = useState(false);
    const [currentAbout, setCurrentAbout] = useState("");

    // Effect to update modal state when the viewed profile data changes
    useEffect(() => {
        if (viewedProfile?.about) {
            setCurrentAbout(viewedProfile.about);
        }
    }, [viewedProfile?.about]);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const currentURL = window.location.href;

    const handleShareClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    // Experience modal handlers
    const handleOpenExperienceModal = (index?: number) => {
        setExperienceErrors({}); // Clear errors when opening
        if (index !== undefined && viewedProfile?.experience?.[index]) {
            // Edit existing experience
            setCurrentExperience(viewedProfile.experience[index]);
            setEditExperienceIndex(index);
        } else {
            // Add new experience
            setCurrentExperience({
                companyName: "",
                jobTitle: "",
                employmentType: "",
                country: "",
                locationType: "",
                currentlyWorkHere: false,
                startDate: "",
                endDate: "",
                description: "",
            });
            setEditExperienceIndex(null);
        }
        setExperienceModalOpen(true);
    };

    const handleCloseExperienceModal = () => {
        setExperienceModalOpen(false);
        setExperienceErrors({}); // Clear errors when closing
    };

    const handleExperienceChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target;
        let processedValue = value;

        // Capitalize first letter for specific fields
        if ((name === "jobTitle" || name === "companyName") && value.length > 0) {
            processedValue = value.charAt(0).toUpperCase() + value.slice(1);
        }

        setCurrentExperience((prev) => ({
            ...prev,
            [name]: processedValue, // Use the processed value
        }));
    };

    const handleExperienceSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCurrentExperience((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleExperienceSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentExperience((prev) => ({
            ...prev,
            currentlyWorkHere: e.target.checked,
            endDate: e.target.checked ? "" : prev.endDate,
        }));
    };

    const handleSaveExperience = () => {
        const errors: Partial<Record<keyof Experience, string>> = {};

        // Basic validation
        if (!currentExperience.jobTitle) errors.jobTitle = "Job title is required.";
        if (!currentExperience.companyName) errors.companyName = "Company name is required.";
        if (!currentExperience.employmentType)
            errors.employmentType = "Employment type is required.";
        if (!currentExperience.country) errors.country = "Country is required.";
        if (!currentExperience.startDate) errors.startDate = "Start date is required.";

        // Date validation: endDate should not be earlier than startDate
        if (
            !currentExperience.currentlyWorkHere &&
            currentExperience.endDate &&
            currentExperience.startDate &&
            new Date(currentExperience.endDate) < new Date(currentExperience.startDate)
        ) {
            // Assign error to endDate, even if it's valid on its own, because the relationship is wrong
            errors.endDate = "End date cannot be earlier than the start date.";
        }

        setExperienceErrors(errors);

        // If there are errors, stop the save process
        if (Object.keys(errors).length > 0) {
            return;
        }

        // --- Save logic (if validation passes) ---
        const saveAndUpdateExperience =
            () => async (dispatch: AppDispatch, getState: () => RootState) => {
                // Create a mutable copy
                let finalExperience: Experience = { ...currentExperience };

                // If currently working here, ensure endDate is handled appropriately
                if (finalExperience.currentlyWorkHere) {
                    finalExperience = { ...finalExperience, endDate: "" };
                }

                // Dispatch the appropriate synchronous action based on editing or adding
                if (editExperienceIndex !== null) {
                    // Update existing experience
                    dispatch(
                        updateExperience({
                            index: editExperienceIndex,
                            experience: finalExperience,
                        }),
                    );
                } else {
                    // Add new experience
                    dispatch(addExperience(finalExperience));
                }

                // Get latest state
                const latestProfileState = getState().profile;
                const currentToken = getState().auth.token;

                // Dispatch backend update
                if (currentToken) {
                    try {
                        await dispatch(
                            updateProfile({ token: currentToken, profileData: latestProfileState }),
                        );
                    } catch (error) {
                        console.error("Failed to update experience on backend:", error);
                    }
                }
            };

        dispatch(saveAndUpdateExperience());
        handleCloseExperienceModal();
    };

    // Education modal handlers
    const handleOpenEducationModal = (index?: number) => {
        setEducationErrors({}); // Clear errors when opening
        if (index !== undefined && viewedProfile?.education?.[index]) {
            // Edit existing education
            setCurrentEducation(viewedProfile.education[index]);
            setEditEducationIndex(index);
        } else {
            // Add new education
            setCurrentEducation({
                school: "",
                degree: "",
                fieldOfStudy: "",
                country: "",
                grade: "",
                current: false,
                from: "",
                to: "",
                description: "",
            });
            setEditEducationIndex(null);
        }
        setEducationModalOpen(true);
    };

    const handleCloseEducationModal = () => {
        setEducationModalOpen(false);
        setEducationErrors({}); // Clear errors when closing
    };

    const handleEducationChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target;
        setCurrentEducation((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleEducationSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCurrentEducation((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleEducationSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentEducation((prev) => ({
            ...prev,
            current: e.target.checked,
            to: e.target.checked ? "" : prev.to,
        }));
    };

    const handleSaveEducation = () => {
        const errors: Partial<Record<keyof Education | "country", string>> = {};

        // Basic validation using correct property names
        if (!currentEducation.school) errors.school = "School is required.";
        if (!currentEducation.degree) errors.degree = "Degree is required.";
        if (!currentEducation.fieldOfStudy) errors.fieldOfStudy = "Field of study is required.";
        if (!currentEducation.from) errors.from = "Start date (From) is required.";

        // Date validation: 'to' date should not be earlier than 'from' date
        if (
            !currentEducation.current &&
            currentEducation.to &&
            currentEducation.from &&
            new Date(currentEducation.to) < new Date(currentEducation.from)
        ) {
            errors.to = "End date (To) cannot be earlier than the start date (From).";
        }

        setEducationErrors(errors);

        // If there are errors, stop the save process
        if (Object.keys(errors).length > 0) {
            return;
        }

        // --- Save logic (if validation passes) ---
        const saveAndUpdateEducation =
            () => async (dispatch: AppDispatch, getState: () => RootState) => {
                // Create a mutable copy
                let finalEducation: Education = { ...currentEducation };

                // If currently attending, handle the 'to' date appropriately
                if (finalEducation.current) {
                    finalEducation = { ...finalEducation, to: "" };
                }

                // Dispatch the appropriate synchronous action
                if (editEducationIndex !== null) {
                    // Update existing education
                    dispatch(
                        updateEducation({
                            index: editEducationIndex,
                            education: finalEducation,
                        }),
                    );
                } else {
                    // Add new education
                    dispatch(setEducation(finalEducation));
                }

                // 2. Get latest state
                const latestProfileState = getState().profile;
                const currentToken = getState().auth.token;

                // 3. Dispatch backend update
                if (currentToken) {
                    try {
                        await dispatch(
                            updateProfile({ token: currentToken, profileData: latestProfileState }),
                        );
                    } catch (error) {
                        console.error("Failed to update education on backend:", error);
                    }
                }
            };

        dispatch(saveAndUpdateEducation());
        handleCloseEducationModal();
    };

    // Volunteer Experience modal handlers
    const handleOpenVolunteerModal = (index?: number) => {
        setVolunteerErrors({}); // Clear errors when opening
        if (index !== undefined && viewedProfile?.volunteerExperience?.[index]) {
            // Edit existing volunteer experience
            setCurrentVolunteer(viewedProfile.volunteerExperience[index]);
            setEditVolunteerIndex(index);
        } else {
            // Add new volunteer experience
            setCurrentVolunteer({
                organization: "",
                role: "",
                cause: "",
                currentlyVolunteerHere: false,
                startDate: "",
                endDate: "",
                description: "",
            });
            setEditVolunteerIndex(null);
        }
        setVolunteerModalOpen(true);
    };

    const handleCloseVolunteerModal = () => {
        setVolunteerModalOpen(false);
        setVolunteerErrors({}); // Clear errors when closing
    };

    const handleVolunteerChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target;
        setCurrentVolunteer((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleVolunteerSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCurrentVolunteer((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleVolunteerSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentVolunteer((prev) => ({
            ...prev,
            currentlyVolunteerHere: e.target.checked,
            endDate: e.target.checked ? "" : prev.endDate,
        }));
    };

    const handleSaveVolunteer = () => {
        const errors: Partial<Record<keyof VolunteerExperience, string>> = {};

        // Basic validation
        if (!currentVolunteer.organization) errors.organization = "Organization is required.";
        if (!currentVolunteer.role) errors.role = "Role is required.";
        if (!currentVolunteer.cause) errors.cause = "Cause is required.";
        if (!currentVolunteer.startDate) errors.startDate = "Start date is required.";

        // Date validation: endDate should not be earlier than startDate
        if (
            !currentVolunteer.currentlyVolunteerHere &&
            currentVolunteer.endDate &&
            currentVolunteer.startDate &&
            new Date(currentVolunteer.endDate) < new Date(currentVolunteer.startDate)
        ) {
            errors.endDate = "End date cannot be earlier than the start date.";
        }

        setVolunteerErrors(errors);

        // If there are errors, stop the save process
        if (Object.keys(errors).length > 0) {
            return;
        }

        // --- Save logic (if validation passes) ---
        const saveAndUpdateVolunteer =
            () => async (dispatch: AppDispatch, getState: () => RootState) => {
                // Create a mutable copy
                let finalVolunteer: VolunteerExperience = { ...currentVolunteer };

                // If currently volunteering, handle endDate
                if (finalVolunteer.currentlyVolunteerHere) {
                    finalVolunteer = { ...finalVolunteer, endDate: "" };
                }

                // Dispatch synchronous actions
                if (editVolunteerIndex !== null) {
                    // Update existing volunteer experience
                    dispatch(
                        updateVolunteerExperience({
                            index: editVolunteerIndex,
                            volunteerExperience: finalVolunteer,
                        }),
                    );
                } else {
                    // Add new volunteer experience
                    dispatch(addVolunteerExperience(finalVolunteer));
                }

                // Get latest state
                const latestProfileState = getState().profile;
                const currentToken = getState().auth.token;

                // Dispatch backend update
                if (currentToken) {
                    try {
                        await dispatch(
                            updateProfile({ token: currentToken, profileData: latestProfileState }),
                        );
                    } catch (error) {
                        console.error("Failed to update volunteer experience on backend:", error);
                    }
                }
            };

        dispatch(saveAndUpdateVolunteer());
        handleCloseVolunteerModal();
    };

    // License/Certification modal handlers
    const handleOpenLicenseModal = (index?: number) => {
        if (index !== undefined && viewedProfile?.licenseCertification?.[index]) {
            // Edit existing license
            setCurrentLicense(viewedProfile.licenseCertification[index]);
            setEditLicenseIndex(index);
        } else {
            // Add new license
            setCurrentLicense({
                name: "",
                issuingOrganization: "",
                issueDate: "",
                expirationDate: "",
                credentialID: "",
                credentialURL: "",
                neverExpires: false,
            });
            setEditLicenseIndex(null);
        }
        setLicenseModalOpen(true);
    };

    const handleCloseLicenseModal = () => {
        setLicenseModalOpen(false);
    };

    const handleLicenseChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setCurrentLicense((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleLicenseSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentLicense((prev) => ({
            ...prev,
            neverExpires: e.target.checked,
            expirationDate: e.target.checked ? "" : prev.expirationDate,
        }));
    };

    const handleSaveLicense = () => {
        const saveAndUpdateLicense =
            () => async (dispatch: AppDispatch, getState: () => RootState) => {
                // 1. Dispatch local update
                if (editLicenseIndex !== null) {
                    dispatch(
                        updateLicenseCertification({
                            index: editLicenseIndex,
                            licenseCertification: currentLicense,
                        }),
                    );
                } else {
                    dispatch(addLicenseCertification(currentLicense));
                }

                // 2. Get latest state
                const latestProfileState = getState().profile;
                const currentToken = getState().auth.token;

                // 3. Dispatch backend update
                if (currentToken) {
                    try {
                        await dispatch(
                            updateProfile({ token: currentToken, profileData: latestProfileState }),
                        );
                    } catch (error) {
                        console.error("Failed to update license/certification on backend:", error);
                    }
                }
            };

        dispatch(saveAndUpdateLicense());
        setLicenseModalOpen(false);
    };

    // Function to check if a date is in the past (for license expiration)
    const isExpired = (expirationDate: string): boolean => {
        if (!expirationDate) return false;
        const today = new Date();
        const expDate = new Date(expirationDate);
        return expDate < today;
    };

    // Handler for sending the initial connect request
    const handleConnectClick = () => {
        // Prevent action if required data is missing or connecting with self
        if (!loggedInUserId || !profileUserId || !token || loggedInUserId === profileUserId) {
            console.error(
                "Cannot send connection request: Missing IDs, token, or trying to connect with self.",
            );
            return;
        }

        // Prevent sending if already connected or request is pending
        if (connection?.status === "ACCEPTED" || connection?.status === "PENDING") {
            // console.log("Connection already exists or is pending.");
            return;
        }

        // Prevent sending if already sending
        if (initialConnectionRequestStatus === "loading") {
            // console.log("Connection request already in progress.");
            return;
        }

        // Dispatch the send request action
        dispatch(
            sendConnectionRequest({
                requesterId: loggedInUserId,
                recipientId: profileUserId,
                token,
            }),
        );
    };

    // NEW: Handlers for Accept/Decline actions
    const handleAccept = () => {
        if (token && connection?.connectionId && !updateLoading[connection.connectionId]) {
            dispatch(
                updateConnectionStatus({
                    connectionId: connection.connectionId,
                    status: "ACCEPTED",
                    token,
                }),
            );
        }
    };

    const handleDecline = () => {
        if (token && connection?.connectionId && !updateLoading[connection.connectionId]) {
            dispatch(
                updateConnectionStatus({
                    connectionId: connection.connectionId,
                    status: "DECLINED",
                    token,
                }),
            );
        }
    };

    // --- About Modal Handlers ---
    const handleOpenAboutModal = () => {
        // Initialize text field with current about text from Redux state
        setCurrentAbout(viewedProfile?.about || "");
        setAboutModalOpen(true);
    };

    const handleCloseAboutModal = () => {
        setAboutModalOpen(false);
    };

    const handleAboutChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        // Enforce character limit
        setCurrentAbout(e.target.value.slice(0, 500));
    };

    const handleSaveAbout = () => {
        const saveAndUpdateAbout =
            () => async (dispatch: AppDispatch, getState: () => RootState) => {
                // 1. Dispatch local update
                dispatch(setAbout(currentAbout));

                // 2. Get latest state
                const latestProfileState = getState().profile;
                const currentToken = getState().auth.token;

                // 3. Dispatch backend update
                if (currentToken) {
                    try {
                        await dispatch(
                            updateProfile({ token: currentToken, profileData: latestProfileState }),
                        );
                    } catch (error) {
                        console.error("Failed to update about section on backend:", error);
                        // Optionally show a toast error to the user
                    }
                }
            };

        dispatch(saveAndUpdateAbout());
        handleCloseAboutModal(); // Close the modal
    };

    // --- Handlers for Message Modal ---
    const handleOpenMessageModal = () => {
        // console.log("[Profile] Opening message modal with auth state:", {
        //     isAuthenticated: !!token,
        //     tokenLength: token?.length || 0,
        //     userId: user?.id,
        //     user: user,
        // });
        setMessageModalOpen(true);
    };

    const handleCloseMessageModal = () => {
        setMessageModalOpen(false);
    };

    // --- New Handler for Direct Messaging ---
    const handleMessageUser = async () => {
        console.log("=== [Profile] handleMessageUser called ===");
        console.log("[Profile] loggedInUserId:", loggedInUserId);
        console.log("[Profile] profileUserId:", profileUserId);
        console.log("[Profile] token exists:", !!token);

        if (!loggedInUserId || !profileUserId || !token || loggedInUserId === profileUserId) {
            console.error(
                "[Profile] Cannot start conversation: Missing IDs, token, or trying to message self.",
            );
            console.error("[Profile] Validation failed:", {
                loggedInUserId,
                profileUserId,
                hasToken: !!token,
                isSameUser: loggedInUserId === profileUserId,
            });
            return;
        }

        try {
            console.log(`[Profile] Starting conversation with user: ${profileUserId}`);

            // Find or create conversation with the user
            console.log("[Profile] Dispatching findOrCreateConversation...");
            const resultAction = await dispatch(
                findOrCreateConversation({ recipientId: profileUserId }),
            );

            console.log("[Profile] findOrCreateConversation result:", resultAction);

            // Check if the action was successful
            if (findOrCreateConversation.fulfilled.match(resultAction)) {
                const conversation = resultAction.payload;
                console.log(`[Profile] ✅ Conversation found/created successfully:`, conversation);
                console.log(`[Profile] Conversation ID: ${conversation.id}`);

                // Set the conversation as active
                console.log("[Profile] Setting conversation as active...");
                dispatch(setActiveConversation(conversation.id));
                console.log(`[Profile] ✅ Active conversation set to: ${conversation.id}`);

                // Add a small delay to ensure Redux state is updated
                await new Promise((resolve) => setTimeout(resolve, 100));

                // Navigate to messaging page
                console.log("[Profile] Navigating to /messaging...");
                navigate("/messaging");
                console.log("[Profile] ✅ Navigation completed");
            } else if (findOrCreateConversation.rejected.match(resultAction)) {
                // Handle the case where conversation already exists
                interface ConversationRejectedPayload {
                    status?: number;
                    existingConversationId?: string;
                    message?: string;
                }
                const payload = resultAction.payload as ConversationRejectedPayload;
                console.log("[Profile] ⚠️ findOrCreateConversation was rejected:", payload);

                if (payload?.status === 409 && payload?.existingConversationId) {
                    console.log(
                        `[Profile] 🔄 Using existing conversation: ${payload.existingConversationId}`,
                    );

                    // Set the existing conversation as active
                    console.log("[Profile] Setting existing conversation as active...");
                    dispatch(setActiveConversation(payload.existingConversationId));
                    console.log(
                        `[Profile] ✅ Active conversation set to: ${payload.existingConversationId}`,
                    );

                    // Add a small delay to ensure Redux state is updated
                    await new Promise((resolve) => setTimeout(resolve, 100));

                    // Navigate to messaging page
                    console.log("[Profile] Navigating to /messaging...");
                    navigate("/messaging");
                    console.log("[Profile] ✅ Navigation completed");
                } else {
                    console.error("[Profile] ❌ Failed to start conversation:", payload?.message);
                    console.error("[Profile] Full error payload:", payload);
                }
            } else {
                console.error(
                    "[Profile] ❌ Unexpected result from findOrCreateConversation:",
                    resultAction,
                );
            }
        } catch (error) {
            console.error("[Profile] ❌ Error starting conversation:", error);
        }

        console.log("=== [Profile] handleMessageUser completed ===");
    };

    // --- Handlers for Import Profile Modal ---
    const handleOpenImportProfileModal = () => {
        setImportProfileModalOpen(true);
    };

    const handleCloseImportProfileModal = () => {
        setImportProfileModalOpen(false);
    };

    // --- Render Action Buttons Logic --- (Refactored)
    const renderActionButtons = () => {
        // Add logs here for debugging
        // // console.log("---------------------");
        // // console.log("[RenderButtons] Start Check");
        // // console.log("[RenderButtons] isOwnProfile:", isOwnProfile);
        // // console.log("[RenderButtons] statusLoading:", statusLoading);
        // // console.log("[RenderButtons] connection:", JSON.stringify(connection)); // Log full connection object
        // // console.log("[RenderButtons] loggedInUserId:", loggedInUserId);
        // // console.log("[RenderButtons] profileUserId (viewed user):", profileUserId);

        // 1. Own Profile: Show Edit Button (No other buttons needed here for own profile view in this function)
        // The main return outside this function handles the layout for own profile
        if (isOwnProfile) {
            // This function is primarily for rendering buttons on OTHER users' profiles.
            // The logic for own profile (Edit + Share) is handled directly in the main return.
            return null;
        }

        // 2. Loading Connection Status: Show Spinner
        if (statusLoading) {
            // // console.log("[RenderButtons] Decision: Status Loading -> Spinner");
            return <CircularProgress size={24} />;
        }

        // 3. Connection Exists: Handle based on status
        if (connection) {
            // // console.log("[RenderButtons] Condition: Connection object exists.");
            const isUpdateOpLoading = updateLoading[connection.connectionId] === true;
            // console.log(`[RenderButtons]   Status: ${connection.status}`);
            // console.log(`[RenderButtons]   Requester ID: ${connection.requesterId}`);
            // console.log(`[RenderButtons]   Recipient ID: ${connection.recipientId}`);
            // console.log(
            //     `[RenderButtons]   Is Update Operation Loading? ${isUpdateOpLoading} for ID: ${connection.connectionId}`,
            // );

            if (connection.status === "ACCEPTED") {
                // // console.log("[RenderButtons] Decision: Status ACCEPTED -> Connected Button");
                // Message button is rendered outside this function, so we don't need a placeholder here.
                return (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Button
                            variant="contained"
                            disabled
                            startIcon={<CheckIcon />}
                            sx={{ color: "#fff" }}
                        >
                            Connected
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<ChatBubbleOutline />}
                            onClick={handleMessageUser}
                            sx={{ color: "#fff" }}
                        >
                            Message
                        </Button>
                    </Box>
                );
            }

            if (connection.status === "PENDING") {
                // // console.log("[RenderButtons] Condition: Status is PENDING.");
                // If logged-in user sent the request
                if (loggedInUserId === connection.requesterId) {
                    // console.log(
                    //     "[RenderButtons] Decision: PENDING & Logged-in user is Requester -> Request Sent Button",
                    // );
                    return (
                        <Button variant="contained" disabled>
                            Request Sent
                        </Button>
                    );
                }
                // If logged-in user received the request
                else if (loggedInUserId === connection.recipientId) {
                    // console.log(
                    //     "[RenderButtons] Decision: PENDING & Logged-in user is Recipient -> Accept/Decline Buttons",
                    // );
                    return (
                        <Box display="flex" gap={1}>
                            <Button
                                variant="outlined"
                                onClick={handleDecline}
                                disabled={isUpdateOpLoading}
                                size="small"
                                startIcon={
                                    isUpdateOpLoading ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <CloseIcon />
                                    )
                                }
                            >
                                Decline
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleAccept}
                                disabled={isUpdateOpLoading}
                                size="small"
                                startIcon={
                                    isUpdateOpLoading ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <CheckIcon />
                                    )
                                }
                            >
                                Accept
                            </Button>
                        </Box>
                    );
                }
            }
            // Fall-through if status is DECLINED or PENDING with mismatched IDs
        } else {
            // console.log(
            //     "[RenderButtons] Condition: No connection object exists (null or undefined).",
            // );
        }

        // 4. No Connection or DECLINED: Show Connect Button (with initial send status)
        // // console.log("[RenderButtons] Reaching Connect Button Logic Block");
        let connectButtonText = "Connect";
        let isConnectButtonDisabled = false;
        let connectButtonIcon = <AddIcon />;

        // console.log(
        //     `[RenderButtons]   Initial Send Request Status: ${initialConnectionRequestStatus}`,
        // );
        if (initialConnectionRequestStatus === "loading") {
            connectButtonText = "Sending...";
            isConnectButtonDisabled = true;
            connectButtonIcon = <CircularProgress size={20} color="inherit" />;
        } else if (initialConnectionRequestStatus === "succeeded") {
            // After sending, show Request Sent until fetchConnectionStatus updates state
            connectButtonText = "Request Sent";
            isConnectButtonDisabled = true;
            connectButtonIcon = <CheckIcon />;
        } else if (initialConnectionRequestStatus === "failed") {
            connectButtonText = "Connect"; // Allow retry on failure
        }

        // console.log(
        //     `[RenderButtons] Decision: Fall-through -> Connect Button (Text='${connectButtonText}', Disabled=${isConnectButtonDisabled})`,
        // );
        return (
            <Button
                variant="contained"
                className="connect-button"
                endIcon={connectButtonIcon}
                onClick={handleConnectClick}
                disabled={isConnectButtonDisabled}
            >
                {connectButtonText}
            </Button>
        );
    };

    // Background photo upload handler
    const handleBackgroundPhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && token) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result as string;
                // Dispatching the action is sufficient. Redux state change will trigger re-render.
                dispatch(uploadProfileBackgroundPhoto({ token, photoData: base64String }));
            };
            reader.readAsDataURL(file);
        }
        if (event.target) {
            event.target.value = "";
        }
    };

    // Profile photo upload handler
    const handleProfilePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && token) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result as string;
                // Dispatching the action is sufficient. Redux state change will trigger re-render.
                dispatch(uploadProfilePhoto({ token, photoData: base64String }));
            };
            reader.readAsDataURL(file);
        }
        if (event.target) {
            event.target.value = "";
        }
    };

    // Ref for hidden file input (background photo)
    const backgroundPhotoInputRef = React.useRef<HTMLInputElement>(null);

    // Show loading indicator while profile data is loading
    if (isProfileLoading || !viewedProfile) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>
                    Loading profile...
                </Typography>
            </Box>
        );
    }

    const handlePostClick = () => {
        // Store a flag in sessionStorage to indicate we want to open the post modal
        sessionStorage.setItem("openPostModal", "true");
        // Navigate to the newsfeed
        navigate("/newsfeed");
    };

    // --- NEW: Handlers for Delete Confirmation ---
    const handleOpenConfirmDelete = (type: string, index: number) => {
        setItemToDelete({ type, index });
        setConfirmDeleteModalOpen(true);
    };

    const handleCloseConfirmDelete = () => {
        setConfirmDeleteModalOpen(false);
        setItemToDelete(null);
    };

    const handleConfirmDelete = () => {
        if (!itemToDelete || !token) return;

        const { type, index } = itemToDelete;

        const deleteItemAndSaveChanges =
            () => async (dispatch: AppDispatch, getState: () => RootState) => {
                // 1. Dispatch the appropriate local delete action
                switch (type) {
                    case "experience":
                        dispatch(deleteExperience(index));
                        break;
                    case "education":
                        dispatch(deleteEducation(index));
                        break;
                    case "volunteer":
                        dispatch(deleteVolunteerExperience(index));
                        break;
                    case "license":
                        dispatch(deleteLicenseCertification(index));
                        break;
                    default:
                        return;
                }

                // 2. Get latest state after deletion
                const latestProfileState = getState().profile;
                const currentToken = getState().auth.token;

                // 3. Dispatch backend update with the new state
                if (currentToken) {
                    try {
                        await dispatch(
                            updateProfile({ token: currentToken, profileData: latestProfileState }),
                        );
                    } catch (error) {
                        console.error(
                            `Failed to update profile on backend after deleting ${type}:`,
                            error,
                        );
                    }
                }
            };

        dispatch(deleteItemAndSaveChanges());

        // Close all relevant modals
        handleCloseConfirmDelete();
        switch (type) {
            case "experience":
                handleCloseExperienceModal();
                break;
            case "education":
                handleCloseEducationModal();
                break;
            case "volunteer":
                handleCloseVolunteerModal();
                break;
            case "license":
                handleCloseLicenseModal();
                break;
        }
    };

    return (
        <div className="profile-wrapper hi-fi-profile">
            {/* Islamic background pattern */}
            <div className="islamic-background"></div>

            {/* Profile and Cover Picture */}
            <div className="profile-cover-section">
                <div className="cover-photo">
                    <img
                        src={viewedProfile.backgroundPhotoUrl || aspireProfileBackground}
                        alt="Cover"
                        className="cover-photo-image"
                    />
                    {isOwnProfile && (
                        <IconButton
                            className="edit-cover-photo"
                            aria-label="Edit Cover Photo"
                            component="label"
                        >
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleBackgroundPhotoChange}
                            />
                            <EditIcon />
                        </IconButton>
                    )}
                </div>
                {/* <div className="profile-avatar">
                    {profilePhoto ? (
                        <img src={profilePhoto} alt="Profile" />
                    ) : (
                        <Avatar sx={{ width: "100%", height: "100%", fontSize: "64px" }}>
                            {profile.firstName ? profile.firstName.charAt(0) : "?"}
                        </Avatar>
                    )}
                    {isOwnProfile && (
                        <IconButton
                            className="edit-avatar"
                            aria-label="Change Profile Picture"
                            component="label"
                        >
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleProfilePhotoChange}
                            />
                            <EditIcon />
                        </IconButton>
                    )}
                </div> */}
            </div>

            {/* Main content using grid layout */}
            <div className="profile-content-grid">
                {/* Left column - Profile info and People you may know */}
                <div className="profile-left-column">
                    {/* Profile Info */}
                    <div className="profile-info-card">
                        <div className="profile-info-content">
                            <div className="profile-avatar">
                                {viewedProfile.photoUrl ? (
                                    <img src={viewedProfile.photoUrl} alt="Profile" />
                                ) : (
                                    <Avatar
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            fontSize: "64px",
                                        }}
                                    >
                                        {viewedProfile.firstName
                                            ? viewedProfile.firstName.charAt(0)
                                            : "?"}
                                    </Avatar>
                                )}
                            </div>
                            <div className="profile-info-details">
                                <Typography className="profile-name">
                                    {getFullName(viewedProfile.firstName, viewedProfile.lastName)}
                                </Typography>

                                <div className="profile-attributes">
                                    <Typography className="profile-title">
                                        {viewedProfile.title || "No Title"}
                                    </Typography>
                                    <Typography className="profile-location">
                                        {isOwnProfile
                                            ? viewedProfile.location
                                                ? `${viewedProfile.location.state}, ${viewedProfile.location.country}`
                                                : "No Location"
                                            : typeof viewedProfile.location === "string"
                                              ? viewedProfile.location
                                              : viewedProfile.location
                                                ? `${viewedProfile.location.state}, ${viewedProfile.location.country}`
                                                : "No Location"}
                                    </Typography>
                                    <Typography className="profile-portfolio">
                                        Portfolio / Resume
                                    </Typography>
                                    {viewedProfile.linkedinURL && (
                                        <IconButton
                                            component="a"
                                            href={viewedProfile.linkedinURL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="LinkedIn Profile"
                                            sx={{ p: 0, mt: 1, width: "fit-content" }}
                                        >
                                            <LinkedInIcon sx={{ color: "#0077b5", fontSize: 28 }} />
                                        </IconButton>
                                    )}
                                </div>
                            </div>

                            <div className="action-buttons">
                                {isOwnProfile ? (
                                    <>
                                        <Link to="/settings" style={{ textDecoration: "none" }}>
                                            <Button
                                                className="action-button"
                                                startIcon={<EditIcon />}
                                            >
                                                Edit Profile
                                            </Button>
                                        </Link>
                                        <Button
                                            className="action-button"
                                            startIcon={<CloudUploadIcon />}
                                            onClick={handleOpenImportProfileModal}
                                        >
                                            Import Profile/Resume
                                        </Button>
                                        <Button
                                            className="action-button"
                                            startIcon={<Share />}
                                            onClick={handleShareClick}
                                        >
                                            Share Profile
                                        </Button>
                                    </>
                                ) : (
                                    renderActionButtons()
                                )}
                            </div>
                            {/* <Typography className="section-description">
                                {viewedProfile.about ||
                                    "Add your about section to tell people more about yourself."}
                            </Typography> */}
                        </div>
                    </div>

                    {/* People You May Know */}
                    <div className="people-section">
                        <div className="people-card">
                            <Typography className="people-title">People You May Know</Typography>
                            <div className="people-list">
                                {suggestionsStatus === "loading" && <CircularProgress />}
                                {suggestionsStatus === "failed" && (
                                    <Typography color="error">
                                        Could not load suggestions.
                                    </Typography>
                                )}
                                {suggestionsStatus === "succeeded" &&
                                    (suggestedUsers.length > 0 ? (
                                        suggestedUsers.map((person) => (
                                            <div className="person-item" key={person.id}>
                                                <div className="person-avatar">
                                                    <Avatar
                                                        src={person.profileImageUrl}
                                                        sx={{ width: "100%", height: "100%" }}
                                                    >
                                                        {person.name ? person.name.charAt(0) : ""}
                                                    </Avatar>
                                                </div>
                                                <div className="person-info">
                                                    <Typography className="person-name">
                                                        {person.name}
                                                    </Typography>
                                                    <Typography className="person-title">
                                                        {person.title || person.aboutSnippet}
                                                    </Typography>
                                                    <Link
                                                        to={`/profile/${person.id}`}
                                                        style={{ textDecoration: "none" }}
                                                    >
                                                        <Button className="person-connect-button">
                                                            View Profile
                                                        </Button>
                                                    </Link>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <Typography>No suggestions available.</Typography>
                                    ))}
                            </div>
                            <Link
                                to="/people"
                                style={{
                                    textDecoration: "none",
                                    width: "80%",
                                    margin: "1rem auto 0",
                                }}
                            >
                                <Button className="view-more-button">View More</Button>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Right column - Content sections */}
                <div className="profile-right-column">
                    {/* About Section */}
                    <div className="section-card about-section">
                        <div className="section-content">
                            <div className="section-header">
                                <Typography className="section-title">About</Typography>
                                {isOwnProfile && (
                                    <IconButton
                                        className="edit-icon"
                                        onClick={handleOpenAboutModal}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                )}
                            </div>
                            <Typography className="section-description">
                                {viewedProfile.about ||
                                    "Add your about section to tell people more about yourself."}
                            </Typography>
                        </div>
                    </div>

                    {/* Activities Section */}
                    <div className="section-card activities-section">
                        <div className="section-content">
                            <div className="section-header">
                                <Typography className="section-title">Activity</Typography>
                                {isOwnProfile && (
                                    <div className="header-actions">
                                        <Button
                                            className="small-action-button"
                                            onClick={handlePostClick}
                                        >
                                            Post
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <Typography className="section-description">
                                No Activities Yet...
                            </Typography>
                        </div>
                    </div>

                    {/* Experience Section */}
                    <div className="section-card experience-section">
                        <div className="section-content">
                            <div className="section-header">
                                <Typography className="section-title">Experience</Typography>
                                {isOwnProfile && (
                                    <IconButton
                                        className="edit-icon"
                                        onClick={() => handleOpenExperienceModal()}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                )}
                            </div>
                            {viewedProfile.experience && viewedProfile.experience.length > 0 ? (
                                viewedProfile.experience
                                    .slice() // Create a shallow copy
                                    .sort((a, b) => {
                                        // Prioritize present jobs
                                        if (a.currentlyWorkHere && !b.currentlyWorkHere) return -1; // a (present) comes first
                                        if (!a.currentlyWorkHere && b.currentlyWorkHere) return 1; // b (present) comes first

                                        // If both are present jobs, sort by start date descending (most recent start date first)
                                        if (a.currentlyWorkHere && b.currentlyWorkHere) {
                                            const dateA = new Date(a.startDate);
                                            const dateB = new Date(b.startDate);
                                            return dateB.getTime() - dateA.getTime();
                                        }

                                        // If both are past jobs, sort by end date descending first
                                        // Treat empty/null end dates as earliest
                                        const endDateA = a.endDate
                                            ? new Date(a.endDate).getTime()
                                            : -Infinity;
                                        const endDateB = b.endDate
                                            ? new Date(b.endDate).getTime()
                                            : -Infinity;

                                        if (endDateB !== endDateA) {
                                            return endDateB - endDateA; // Most recent end date first
                                        }

                                        // If end dates are the same for past jobs, sort by start date descending
                                        const startDateA = new Date(a.startDate);
                                        const startDateB = new Date(b.startDate);
                                        return startDateB.getTime() - startDateA.getTime(); // Most recent start date first
                                    })
                                    .map((exp, index) => (
                                        <Box key={index} className="experience-item">
                                            <div>
                                                <Typography
                                                    variant="subtitle1"
                                                    className="experience-jobTitle"
                                                >
                                                    {exp.jobTitle}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    className="experience-companyName"
                                                >
                                                    {exp.companyName}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    className="experience-dates"
                                                >
                                                    {exp.startDate
                                                        ? new Date(
                                                              exp.startDate,
                                                          ).toLocaleDateString("en-US", {
                                                              year: "numeric",
                                                              month: "short",
                                                          })
                                                        : "N/A"}{" "}
                                                    -
                                                    {exp.currentlyWorkHere
                                                        ? " Present"
                                                        : exp.endDate
                                                          ? ` ${new Date(exp.endDate).toLocaleDateString("en-US", { year: "numeric", month: "short" })}`
                                                          : "N/A"}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    className="experience-description"
                                                >
                                                    {exp.description}
                                                </Typography>
                                            </div>
                                            {isOwnProfile && (
                                                <IconButton
                                                    className="edit-item"
                                                    onClick={() => handleOpenExperienceModal(index)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))
                            ) : (
                                <Typography className="section-description">
                                    {isOwnProfile
                                        ? "Add your work experience to showcase your professional journey."
                                        : "No work experience has been added yet."}{" "}
                                </Typography>
                            )}
                        </div>
                    </div>

                    {/* Education Section */}
                    <div className="section-card education-section">
                        <div className="section-content">
                            <div className="section-header">
                                <Typography className="section-title">Education</Typography>
                                {isOwnProfile && (
                                    <IconButton
                                        className="edit-icon"
                                        onClick={() => handleOpenEducationModal()}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                )}
                            </div>
                            {viewedProfile.education && viewedProfile.education.length > 0 ? (
                                viewedProfile.education.map((edu, index) => (
                                    <Box key={index} className="education-item">
                                        <div>
                                            <Typography variant="subtitle1">
                                                {edu.degree}
                                            </Typography>
                                            <Typography variant="body1">{edu.school}</Typography>
                                            <Typography variant="body2">
                                                {edu.from
                                                    ? new Date(edu.from).toLocaleDateString(
                                                          "en-US",
                                                          {
                                                              year: "numeric",
                                                              month: "short",
                                                          },
                                                      )
                                                    : "N/A"}{" "}
                                                -
                                                {edu.current
                                                    ? " Present"
                                                    : edu.to
                                                      ? ` ${new Date(edu.to).toLocaleDateString("en-US", { year: "numeric", month: "short" })}`
                                                      : "N/A"}
                                            </Typography>
                                            {edu.description && (
                                                <Typography variant="body2">
                                                    {edu.description}
                                                </Typography>
                                            )}
                                        </div>
                                        {isOwnProfile && (
                                            <IconButton
                                                className="edit-item"
                                                onClick={() => handleOpenEducationModal(index)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))
                            ) : (
                                <Typography className="section-description">
                                    {isOwnProfile
                                        ? "Add your education details to showcase your academic background."
                                        : "No education details have been added yet."}{" "}
                                </Typography>
                            )}
                        </div>
                    </div>

                    {/* Volunteer Section */}
                    <div className="section-card volunteer-section">
                        <div className="section-content">
                            <div className="section-header">
                                <Typography className="section-title">
                                    Volunteer Experience
                                </Typography>
                                {isOwnProfile && (
                                    <IconButton
                                        className="edit-icon"
                                        onClick={() => handleOpenVolunteerModal()}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                )}
                            </div>
                            {viewedProfile.volunteerExperience &&
                            viewedProfile.volunteerExperience.length > 0 ? (
                                viewedProfile.volunteerExperience.map((volunteer, index) => (
                                    <Box key={index} className="volunteer-item">
                                        <div>
                                            <Typography variant="subtitle1">
                                                {volunteer.role}
                                            </Typography>
                                            <Typography variant="body1">
                                                {volunteer.organization}
                                            </Typography>
                                            <Typography variant="body2">
                                                {volunteer.startDate
                                                    ? new Date(
                                                          volunteer.startDate,
                                                      ).toLocaleDateString("en-US", {
                                                          year: "numeric",
                                                          month: "short",
                                                      })
                                                    : "N/A"}{" "}
                                                -
                                                {volunteer.currentlyVolunteerHere
                                                    ? " Present"
                                                    : volunteer.endDate
                                                      ? ` ${new Date(volunteer.endDate).toLocaleDateString("en-US", { year: "numeric", month: "short" })}`
                                                      : "N/A"}
                                            </Typography>
                                            <Typography variant="body2">
                                                Cause:{" "}
                                                {causeOptions.find(
                                                    (option) => option.value === volunteer.cause,
                                                )?.label || volunteer.cause}
                                            </Typography>
                                            {volunteer.description && (
                                                <Typography variant="body2">
                                                    {volunteer.description}
                                                </Typography>
                                            )}
                                        </div>
                                        {isOwnProfile && (
                                            <IconButton
                                                className="edit-item"
                                                onClick={() => handleOpenVolunteerModal(index)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))
                            ) : (
                                <Typography className="section-description">
                                    {isOwnProfile
                                        ? "Add your volunteering experience to showcase your community involvement."
                                        : "No volunteer experience has been added yet."}
                                </Typography>
                            )}
                        </div>
                    </div>

                    {/* License Section */}
                    <div className="section-card license-section">
                        <div className="section-content">
                            <div className="section-header">
                                <Typography className="section-title">
                                    Licenses & Certifications
                                </Typography>
                                {isOwnProfile && (
                                    <IconButton
                                        className="edit-icon"
                                        onClick={() => handleOpenLicenseModal()}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                )}
                            </div>
                            {viewedProfile.licenseCertification &&
                            viewedProfile.licenseCertification.length > 0 ? (
                                viewedProfile.licenseCertification.map((license, index) => (
                                    <Box key={index} className="license-item">
                                        <div>
                                            <Typography variant="subtitle1">
                                                {license.name}
                                            </Typography>
                                            <Typography variant="body1">
                                                {license.issuingOrganization}
                                            </Typography>
                                            <Typography variant="body2">
                                                Issued:{" "}
                                                {license.issueDate
                                                    ? new Date(
                                                          license.issueDate,
                                                      ).toLocaleDateString("en-US", {
                                                          year: "numeric",
                                                          month: "short",
                                                      })
                                                    : "N/A"}
                                                {license.neverExpires ? (
                                                    <span className="no-expiration">
                                                        {" "}
                                                        • No Expiration
                                                    </span>
                                                ) : license.expirationDate ? (
                                                    isExpired(license.expirationDate) ? (
                                                        <span className="expired">
                                                            {" "}
                                                            • Expired:{" "}
                                                            {new Date(
                                                                license.expirationDate,
                                                            ).toLocaleDateString("en-US", {
                                                                year: "numeric",
                                                                month: "short",
                                                            })}
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            {" "}
                                                            • Expires:{" "}
                                                            {new Date(
                                                                license.expirationDate,
                                                            ).toLocaleDateString("en-US", {
                                                                year: "numeric",
                                                                month: "short",
                                                            })}
                                                        </span>
                                                    )
                                                ) : null}
                                            </Typography>
                                            {license.credentialID && (
                                                <Typography variant="body2">
                                                    Credential ID: {license.credentialID}
                                                </Typography>
                                            )}
                                            {license.credentialURL && (
                                                <Typography variant="body2">
                                                    <a
                                                        href={license.credentialURL}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        View Credential
                                                    </a>
                                                </Typography>
                                            )}
                                        </div>
                                        {isOwnProfile && (
                                            <IconButton
                                                className="edit-item"
                                                onClick={() => handleOpenLicenseModal(index)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))
                            ) : (
                                <Typography className="section-description">
                                    {isOwnProfile
                                        ? "Add your licenses and certifications to showcase your credentials."
                                        : "No licenses or certifications have been added yet."}
                                </Typography>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Keep the existing modals for functionality */}
            <Box sx={{ display: "none" }}>
                <Box className="main-card-container">
                    <Card className="main-card">{/* ... existing card content ... */}</Card>
                </Box>
            </Box>

            {/* All modals */}
            <Modal isVisible={aboutModalOpen} onClose={handleCloseAboutModal} title="About">
                <TextField
                    fullWidth
                    multiline
                    rows={6}
                    variant="outlined"
                    value={currentAbout}
                    onChange={handleAboutChange}
                    placeholder="Tell others about yourself"
                    inputProps={{ maxLength: 500 }}
                    helperText={`${currentAbout.length}/500 characters`}
                />
                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={handleSaveAbout} variant="contained" color="primary">
                        Save
                    </Button>
                </Box>
            </Modal>

            {/* Share Popover */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                <SharePopup url={currentURL} anchorEl={anchorEl} onClose={handleClosePopover} />
            </Popover>

            {/* ... Other modals ... */}

            {isOwnProfile && (
                <>
                    {/* Experience Modal */}
                    <Modal
                        isVisible={experienceModalOpen}
                        onClose={handleCloseExperienceModal}
                        title={editExperienceIndex !== null ? "Edit Experience" : "Add Experience"}
                    >
                        <Box component="form" sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Company Name *"
                                        name="companyName"
                                        placeholder="Ex: Adobe Inc"
                                        value={currentExperience.companyName}
                                        onChange={handleExperienceChange}
                                        required // Keep semantic required, but rely on state for error display
                                        error={!!experienceErrors.companyName}
                                        helperText={experienceErrors.companyName || " "}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Job Title *"
                                        name="jobTitle"
                                        placeholder="Ex: Senior Software Engineer"
                                        value={currentExperience.jobTitle}
                                        onChange={handleExperienceChange}
                                        required // Keep semantic required
                                        error={!!experienceErrors.jobTitle}
                                        helperText={experienceErrors.jobTitle || " "}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Employment Type *" // Added asterisk visually
                                        name="employmentType"
                                        value={currentExperience.employmentType}
                                        onChange={handleExperienceSelectChange}
                                        error={!!experienceErrors.employmentType}
                                        helperText={experienceErrors.employmentType || " "}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                            "& .MuiMenuItem-root": {
                                                color: "var(--text-color)",
                                            },
                                        }}
                                    >
                                        {employmentTypeOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Country *" // Added asterisk visually
                                        name="country"
                                        value={currentExperience.country}
                                        onChange={handleExperienceSelectChange}
                                        error={!!experienceErrors.country}
                                        helperText={experienceErrors.country || " "}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                        }}
                                    >
                                        {countryOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Location Type"
                                        name="locationType"
                                        value={currentExperience.locationType}
                                        onChange={handleExperienceSelectChange}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                        }}
                                    >
                                        {locationTypeOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={currentExperience.currentlyWorkHere}
                                                onChange={handleExperienceSwitchChange}
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                                        color: "var(--primary-color)",
                                                        "& + .MuiSwitch-track": {
                                                            backgroundColor: "var(--primary-light)",
                                                        },
                                                    },
                                                }}
                                            />
                                        }
                                        label="I currently work here"
                                        sx={{ color: "var(--text-color)" }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="Start Date *"
                                            value={
                                                currentExperience.startDate
                                                    ? dayjs(currentExperience.startDate)
                                                    : null
                                            }
                                            onChange={(newDate) =>
                                                setCurrentExperience((prev) => ({
                                                    ...prev,
                                                    startDate: newDate
                                                        ? newDate.format("YYYY-MM-DD")
                                                        : "",
                                                }))
                                            }
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    margin: "normal",
                                                    error: !!experienceErrors.startDate,
                                                    helperText: experienceErrors.startDate || " ",
                                                },
                                            }}
                                            sx={{
                                                width: "100%",
                                                "& .MuiInputBase-input": {
                                                    color: "var(--text-color)",
                                                },
                                                "& .MuiInputLabel-root": {
                                                    color: "var(--text-secondary-color)",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        borderColor: "var(--border-color)",
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: "var(--primary-color)",
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "var(--primary-color)",
                                                    },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="End Date"
                                            value={
                                                currentExperience.endDate
                                                    ? dayjs(currentExperience.endDate)
                                                    : null
                                            }
                                            onChange={(newDate) =>
                                                setCurrentExperience((prev) => ({
                                                    ...prev,
                                                    endDate: newDate
                                                        ? newDate.format("YYYY-MM-DD")
                                                        : "",
                                                }))
                                            }
                                            disabled={currentExperience.currentlyWorkHere}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    margin: "normal",
                                                    error: !!experienceErrors.endDate,
                                                    helperText: experienceErrors.endDate || " ",
                                                },
                                            }}
                                            sx={{
                                                width: "100%",
                                                "& .MuiInputBase-input": {
                                                    color: "var(--text-color)",
                                                },
                                                "& .MuiInputLabel-root": {
                                                    color: "var(--text-secondary-color)",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        borderColor: "var(--border-color)",
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: "var(--primary-color)",
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "var(--primary-color)",
                                                    },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        multiline
                                        rows={4}
                                        value={currentExperience.description}
                                        onChange={handleExperienceChange}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={handleCloseExperienceModal}
                                        sx={{
                                            mr: 1,
                                            color: "var(--text-color)",
                                            borderColor: "var(--border-color)",
                                            "&:hover": {
                                                borderColor: "var(--primary-color)",
                                                backgroundColor: "var(--hover-background)",
                                            },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    {editExperienceIndex !== null && (
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={() =>
                                                handleOpenConfirmDelete(
                                                    "experience",
                                                    editExperienceIndex,
                                                )
                                            }
                                            sx={{ mr: "auto" }} // Pushes to the left
                                        >
                                            Delete
                                        </Button>
                                    )}
                                    <Button
                                        variant="contained"
                                        onClick={handleSaveExperience}
                                        sx={{
                                            backgroundColor: "var(--primary-color)",
                                            color: "white",
                                            "&:hover": {
                                                backgroundColor: "var(--primary-dark)",
                                            },
                                        }}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Modal>

                    {/* Education Modal */}
                    <Modal
                        isVisible={educationModalOpen}
                        onClose={handleCloseEducationModal}
                        title={editEducationIndex !== null ? "Edit Education" : "Add Education"}
                    >
                        <Box component="form" sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="School *"
                                        name="school"
                                        placeholder="Ex: University of Toronto"
                                        value={currentEducation.school}
                                        onChange={handleEducationChange}
                                        required
                                        error={!!educationErrors.school}
                                        helperText={educationErrors.school || " "}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Degree *"
                                        name="degree"
                                        placeholder="Ex: Bachelor's"
                                        value={currentEducation.degree}
                                        onChange={handleEducationChange}
                                        required
                                        error={!!educationErrors.degree}
                                        helperText={educationErrors.degree || " "}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Field of Study *" // Added asterisk
                                        name="fieldOfStudy"
                                        placeholder="Ex: Computer Science"
                                        value={currentEducation.fieldOfStudy}
                                        onChange={handleEducationChange}
                                        error={!!educationErrors.fieldOfStudy}
                                        helperText={educationErrors.fieldOfStudy || " "}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Country"
                                        name="country"
                                        value={currentEducation.country}
                                        onChange={handleEducationSelectChange}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                        }}
                                    >
                                        {countryOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Grade"
                                        name="grade"
                                        placeholder="Ex: 3.8 GPA"
                                        value={currentEducation.grade}
                                        onChange={handleEducationChange}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={currentEducation.current}
                                                onChange={handleEducationSwitchChange}
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                                        color: "var(--primary-color)",
                                                        "& + .MuiSwitch-track": {
                                                            backgroundColor: "var(--primary-light)",
                                                        },
                                                    },
                                                }}
                                            />
                                        }
                                        label="I currently study here"
                                        sx={{ color: "var(--text-color)" }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="From Date *"
                                            value={
                                                currentEducation.from
                                                    ? dayjs(currentEducation.from)
                                                    : null
                                            }
                                            onChange={(newDate) =>
                                                setCurrentEducation((prev) => ({
                                                    ...prev,
                                                    from: newDate
                                                        ? newDate.format("YYYY-MM-DD")
                                                        : "",
                                                }))
                                            }
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    margin: "normal",
                                                    error: !!educationErrors.from,
                                                    helperText: educationErrors.from || " ",
                                                },
                                            }}
                                            sx={{
                                                width: "100%",
                                                "& .MuiInputBase-input": {
                                                    color: "var(--text-color)",
                                                },
                                                "& .MuiInputLabel-root": {
                                                    color: "var(--text-secondary-color)",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        borderColor: "var(--border-color)",
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: "var(--primary-color)",
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "var(--primary-color)",
                                                    },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="To Date"
                                            value={
                                                currentEducation.to
                                                    ? dayjs(currentEducation.to)
                                                    : null
                                            }
                                            onChange={(newDate) =>
                                                setCurrentEducation((prev) => ({
                                                    ...prev,
                                                    to: newDate ? newDate.format("YYYY-MM-DD") : "",
                                                }))
                                            }
                                            disabled={currentEducation.current}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    margin: "normal",
                                                    error: !!educationErrors.to,
                                                    helperText: educationErrors.to || " ",
                                                },
                                            }}
                                            sx={{
                                                width: "100%",
                                                "& .MuiInputBase-input": {
                                                    color: "var(--text-color)",
                                                },
                                                "& .MuiInputLabel-root": {
                                                    color: "var(--text-secondary-color)",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        borderColor: "var(--border-color)",
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: "var(--primary-color)",
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "var(--primary-color)",
                                                    },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        multiline
                                        rows={4}
                                        value={currentEducation.description}
                                        onChange={handleEducationChange}
                                        margin="normal"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                color: "var(--text-color)",
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: "var(--text-secondary-color)",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "var(--border-color)",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "var(--primary-color)",
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={handleCloseEducationModal}
                                        sx={{
                                            mr: 1,
                                            color: "var(--text-color)",
                                            borderColor: "var(--border-color)",
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    {editEducationIndex !== null && (
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={() =>
                                                handleOpenConfirmDelete(
                                                    "education",
                                                    editEducationIndex,
                                                )
                                            }
                                            sx={{ mr: "auto" }}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                    <Button
                                        variant="contained"
                                        onClick={handleSaveEducation}
                                        sx={{
                                            backgroundColor: "var(--primary-color)",
                                            color: "#fff",
                                            "&:hover": { backgroundColor: "var(--primary-dark)" },
                                        }}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Modal>
                </>
            )}

            {/* Volunteer Experience Modal */}
            <Modal
                isVisible={volunteerModalOpen}
                onClose={handleCloseVolunteerModal}
                title={
                    editVolunteerIndex !== null
                        ? "Edit Volunteer Experience"
                        : "Add Volunteer Experience"
                }
            >
                <Box component="form" sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Organization *"
                                name="organization"
                                placeholder="Ex: Red Cross"
                                value={currentVolunteer.organization}
                                onChange={handleVolunteerChange}
                                required
                                error={!!volunteerErrors.organization}
                                helperText={volunteerErrors.organization || " "}
                                margin="normal"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        color: "var(--text-color)",
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "var(--text-secondary-color)",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "var(--border-color)",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Role *"
                                name="role"
                                placeholder="Ex: Community Organizer"
                                value={currentVolunteer.role}
                                onChange={handleVolunteerChange}
                                required
                                error={!!volunteerErrors.role}
                                helperText={volunteerErrors.role || " "}
                                margin="normal"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        color: "var(--text-color)",
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "var(--text-secondary-color)",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "var(--border-color)",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                label="Cause *"
                                name="cause"
                                value={currentVolunteer.cause}
                                onChange={handleVolunteerSelectChange}
                                error={!!volunteerErrors.cause}
                                helperText={volunteerErrors.cause || " "}
                                margin="normal"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        color: "var(--text-color)",
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "var(--text-secondary-color)",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "var(--border-color)",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                    },
                                }}
                            >
                                {causeOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={currentVolunteer.currentlyVolunteerHere}
                                        onChange={handleVolunteerSwitchChange}
                                        sx={{
                                            "& .MuiSwitch-switchBase.Mui-checked": {
                                                color: "var(--primary-color)",
                                                "& + .MuiSwitch-track": {
                                                    backgroundColor: "var(--primary-light)",
                                                },
                                            },
                                        }}
                                    />
                                }
                                label="I currently volunteer here"
                                sx={{ color: "var(--text-color)" }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Start Date *"
                                    value={
                                        currentVolunteer.startDate
                                            ? dayjs(currentVolunteer.startDate)
                                            : null
                                    }
                                    onChange={(newDate) =>
                                        setCurrentVolunteer((prev) => ({
                                            ...prev,
                                            startDate: newDate ? newDate.format("YYYY-MM-DD") : "",
                                        }))
                                    }
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            margin: "normal",
                                            error: !!volunteerErrors.startDate,
                                            helperText: volunteerErrors.startDate || " ",
                                        },
                                    }}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-input": {
                                            color: "var(--text-color)",
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: "var(--text-secondary-color)",
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "var(--border-color)",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="End Date"
                                    value={
                                        currentVolunteer.endDate
                                            ? dayjs(currentVolunteer.endDate)
                                            : null
                                    }
                                    onChange={(newDate) =>
                                        setCurrentVolunteer((prev) => ({
                                            ...prev,
                                            endDate: newDate ? newDate.format("YYYY-MM-DD") : "",
                                        }))
                                    }
                                    disabled={currentVolunteer.currentlyVolunteerHere}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            margin: "normal",
                                            error: !!volunteerErrors.endDate,
                                            helperText: volunteerErrors.endDate || " ",
                                        },
                                    }}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-input": {
                                            color: "var(--text-color)",
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: "var(--text-secondary-color)",
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "var(--border-color)",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                multiline
                                rows={4}
                                value={currentVolunteer.description}
                                onChange={handleVolunteerChange}
                                margin="normal"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        color: "var(--text-color)",
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "var(--text-secondary-color)",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "var(--border-color)",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                        <Button
                            onClick={handleCloseVolunteerModal}
                            sx={{
                                mr: 1,
                                color: "var(--text-color)",
                                borderColor: "var(--border-color)",
                            }}
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                        {editVolunteerIndex !== null && (
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() =>
                                    handleOpenConfirmDelete("volunteer", editVolunteerIndex)
                                }
                                sx={{ mr: "auto" }}
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            onClick={handleSaveVolunteer}
                            sx={{
                                backgroundColor: "var(--primary-color)",
                                color: "#fff",
                                "&:hover": { backgroundColor: "var(--primary-dark)" },
                            }}
                            variant="contained"
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* License/Certification Modal */}
            <Modal
                isVisible={licenseModalOpen}
                onClose={handleCloseLicenseModal}
                title={
                    editLicenseIndex !== null
                        ? "Edit License/Certification"
                        : "Add License/Certification"
                }
            >
                <Box component="form" sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Name *"
                                name="name"
                                placeholder="Ex: AWS Certified Solutions Architect"
                                value={currentLicense.name}
                                onChange={handleLicenseChange}
                                required
                                margin="normal"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        color: "var(--text-color)",
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "var(--text-secondary-color)",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "var(--border-color)",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Issuing Organization *"
                                name="issuingOrganization"
                                placeholder="Ex: Amazon Web Services"
                                value={currentLicense.issuingOrganization}
                                onChange={handleLicenseChange}
                                required
                                margin="normal"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        color: "var(--text-color)",
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "var(--text-secondary-color)",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "var(--border-color)",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={currentLicense.neverExpires}
                                        onChange={handleLicenseSwitchChange}
                                        sx={{
                                            "& .MuiSwitch-switchBase.Mui-checked": {
                                                color: "var(--primary-color)",
                                                "& + .MuiSwitch-track": {
                                                    backgroundColor: "var(--primary-light)",
                                                },
                                            },
                                        }}
                                    />
                                }
                                label="This credential does not expire"
                                sx={{ color: "var(--text-color)" }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Issue Date *"
                                    value={
                                        currentLicense.issueDate
                                            ? dayjs(currentLicense.issueDate)
                                            : null
                                    }
                                    onChange={(newDate) =>
                                        setCurrentLicense((prev) => ({
                                            ...prev,
                                            issueDate: newDate ? newDate.format("YYYY-MM-DD") : "",
                                        }))
                                    }
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            margin: "normal",
                                        },
                                    }}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-input": {
                                            color: "var(--text-color)",
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: "var(--text-secondary-color)",
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "var(--border-color)",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Expiration Date"
                                    value={
                                        currentLicense.expirationDate
                                            ? dayjs(currentLicense.expirationDate)
                                            : null
                                    }
                                    onChange={(newDate) =>
                                        setCurrentLicense((prev) => ({
                                            ...prev,
                                            expirationDate: newDate
                                                ? newDate.format("YYYY-MM-DD")
                                                : "",
                                        }))
                                    }
                                    disabled={currentLicense.neverExpires}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            margin: "normal",
                                        },
                                    }}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-input": {
                                            color: "var(--text-color)",
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: "var(--text-secondary-color)",
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "var(--border-color)",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Credential ID"
                                name="credentialID"
                                placeholder="Enter credential ID (optional)"
                                value={currentLicense.credentialID}
                                onChange={handleLicenseChange}
                                margin="normal"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        color: "var(--text-color)",
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "var(--text-secondary-color)",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "var(--border-color)",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Credential URL"
                                name="credentialURL"
                                placeholder="Enter credential URL (optional)"
                                value={currentLicense.credentialURL}
                                onChange={handleLicenseChange}
                                margin="normal"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        color: "var(--text-color)",
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "var(--text-secondary-color)",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "var(--border-color)",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "var(--primary-color)",
                                        },
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                        <Button
                            onClick={handleCloseLicenseModal}
                            sx={{
                                mr: 1,
                                color: "var(--text-color)",
                                borderColor: "var(--border-color)",
                            }}
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                        {editLicenseIndex !== null && (
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleOpenConfirmDelete("license", editLicenseIndex)}
                                sx={{ mr: "auto" }}
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            onClick={handleSaveLicense}
                            sx={{
                                backgroundColor: "var(--primary-color)",
                                color: "#fff",
                                "&:hover": { backgroundColor: "var(--primary-dark)" },
                            }}
                            variant="contained"
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Import Profile Modal - Render if isOwnProfile is true */}
            {isOwnProfile && token && (
                <ImportProfileService
                    isOpen={isImportProfileModalOpen}
                    onClose={handleCloseImportProfileModal}
                    token={token}
                />
            )}

            {/* Add the NewConversationModal at the end before the SharePopover */}
            {/* Message Modal */}
            <NewConversationModal
                open={isMessageModalOpen}
                onClose={handleCloseMessageModal}
                preselectedUserId={profileUserId} // Changed userId to profileUserId
                preselectedUserName={getFullName(viewedProfile.firstName, viewedProfile.lastName)} // Pass the formatted name
            />

            {/* Share Popover */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                <SharePopup url={currentURL} anchorEl={anchorEl} onClose={handleClosePopover} />
            </Popover>

            {/* NEW: Universal Delete Confirmation Modal */}
            <Modal
                isVisible={isConfirmDeleteModalOpen}
                onClose={handleCloseConfirmDelete}
                title="Confirm Deletion"
            >
                <Typography>
                    Are you sure you want to delete this entry? This action cannot be undone.
                </Typography>
                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <Button variant="outlined" onClick={handleCloseConfirmDelete}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" onClick={handleConfirmDelete}>
                        Delete
                    </Button>
                </Box>
            </Modal>
        </div>
    );
};

export default Profile;
